// Screen 1 — Boost landing page (Figma 1-831).
// Entry from an Instagram boost ad: /submission/boost?bd=0175445&roleid=504827
// This is an OPEN entry page — the querystrings arrive here, so it is never
// gated on flow context or auth. `roleid` (also accepted as `roleId`) is
// optional. It IS gated on the breakdown being open: see load().
// UTM/fbclid params are persisted by RouteWrapper (tData cookie) on mount.

import React, { useEffect } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import {
  IBreakdownRoleDto,
  isMockApi,
} from "../../../shared/api/submission";
import { isNetworkError } from "../../../shared/http";
import { getDeadlineInfo } from "../common/deadline";
import { projectTypeLabel } from "../common/projectTypes";
import { BD_PARAM, parseSubmissionParams } from "../common/submissionParams";
import SubmissionLayout from "../common/SubmissionLayout";
import ScenarioLauncher from "../common/ScenarioLauncher";
import { trialReassurance } from "../common/trialPolicy";
import { trackSubmissionEvent } from "../analytics";
import { openBreakdownUrl } from "../common/openBreakdownUrl";
import { isRoleUnavailable } from "../../../models/submissionFlowModel";
import VerifiedCheck from "../common/VerifiedCheck";
import { membershipPrimary } from "../common/membershipButton";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const closedPathFor = (breakdownId: string | null) =>
  breakdownId
    ? `/submission/closed?${BD_PARAM}=${encodeURIComponent(breakdownId)}`
    : "/submission/closed";

const BoostLandingPage: React.FC<Props> = ({
  history,
  location,
  submissionInfo,
  serverOffsetMs,
  loading,
  error,
  geoGuess,
  geoResolved,
  loadEntry,
  loadGeoGuess,
  startFlow,
}) => {
  // Raw strings — refs are zero-padded (bd=0175445) and must round-trip.
  const { bd: breakdownId, roleid: roleId } = parseSubmissionParams(location.search);

  const load = () => {
    loadEntry({ breakdownId, roleId: roleId || undefined })
      .then((info: any) => {
        // A closed breakdown never renders here: no dead CTA. That covers every
        // closed reason — expired, all roles cast, taken down — and the closed
        // page words each one differently. A cast ROLE on an open breakdown is
        // not a wall: the visitor sees the roles that are still open, with a
        // line saying the one in their link has been filled.
        if (info && info.status !== "Open") {
          history.replace(closedPathFor(breakdownId), {
            breakdownName: info.breakdownName,
            closedReason: info.closedReason,
          });
        }
      })
      .catch((err: any) => {
        if (err && err.status === 404) {
          history.replace("/submission/closed");
        }
        // Other failures render the inline retry state below.
      });
  };

  useEffect(() => {
    // Open entry page: no guard, no auth. `bd` is what an ad link carries; if
    // it is missing there is no specific breakdown to show, so fall back to
    // browsing rather than bouncing the visitor out of the flow.
    if (!breakdownId) {
      history.replace("/submission/casting-calls");
      return;
    }
    trackSubmissionEvent("submission_boost_viewed", { breakdownId, roleId });
    load();
    // The reassurance line below makes a claim about money, and the claim
    // depends on the country. Nothing else on this page needs the guess, but
    // without it the entry page promises a trial to a US visitor who will be
    // told one screen later that submitting is simply free. Cached after the
    // first call, and honours ?ctry= like the rest of the flow.
    loadGeoGuess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!breakdownId) return null;

  const onSelectRole = (role: IBreakdownRoleDto) => {
    trackSubmissionEvent("submission_role_selected", {
      breakdownId,
      roleId: role.roleId,
    });
    startFlow({
      breakdownId,
      roleId: role.roleId,
      entrySource: "boost",
      history,
      search: location.search,
    }).catch(() => {
      // Errors are surfaced through model state; the page re-renders.
    });
  };

  const renderBody = () => {
    if (error) {
      return (
        <ErrorBox role="alert">
          <div>
            {isNetworkError(error)
              ? error.message
              : i18next.t("Something went wrong. Please try again.")}
          </div>
          <RetryButton onClick={load}>{i18next.t("Try again")}</RetryButton>
        </ErrorBox>
      );
    }
    if (loading || !submissionInfo) {
      return (
        <div aria-busy="true">
          <SkeletonBlock height={90} />
          <SkeletonBlock height={64} />
          <SkeletonBlock height={64} />
        </div>
      );
    }

    const deadline = getDeadlineInfo(submissionInfo.closingDateUtc, serverOffsetMs);
    const openRoles = submissionInfo.roles.filter((r) => r.status === "Open");
    const castRoles = submissionInfo.roles.filter((r) => r.status !== "Open");
    // The role the ad pointed at, as the API resolved it (it accepts either ref
    // spelling, so compare its answer, never the raw query value).
    const linkRole = submissionInfo.selectedRole;
    const linkRoleOpen = !!linkRole && linkRole.status === "Open";
    const linkRoleFilled = !!linkRole && !linkRoleOpen;
    // A roleid the breakdown doesn't have (a bad or stale link; the flow sends
    // such links here from any step): say so above the roles it does have.
    const linkRoleUnknown = !!roleId && !linkRole;

    return (
      <>
        <Hero>
          <ProjectType>{projectTypeLabel(submissionInfo.projectType)}</ProjectType>
          <BreakdownName>
            {/* The full breakdown lives on the classic site. Linked only when the
                role in the link can't be had (cast, or not this breakdown's), so
                they can look closer; otherwise the title is plain. A new tab:
                the visitor came here from a link and should not lose this page. */}
            {isRoleUnavailable(submissionInfo, roleId) ? (
              <TitleLink
                href={openBreakdownUrl(submissionInfo.classicRef || submissionInfo.breakdownId)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackSubmissionEvent("submission_casting_call_details_opened", {
                    breakdownId: submissionInfo.breakdownId,
                    from: "boost",
                  })
                }
              >
                {submissionInfo.breakdownName}
                <OpensIcon aria-hidden="true">↗</OpensIcon>
              </TitleLink>
            ) : (
              <TitleText>{submissionInfo.breakdownName}</TitleText>
            )}
            {submissionInfo.isVerifiedDirector && <VerifiedCheck size={18} />}
          </BreakdownName>
          <MetaRow>
            <ClosesPill isClosed={deadline.isClosed && !submissionInfo.isLate} late={!!submissionInfo.isLate}>
              {deadline.label}
            </ClosesPill>
            {submissionInfo.cities[0] && (
              <CityLabel>{submissionInfo.cities[0].cityName}</CityLabel>
            )}
          </MetaRow>
          {submissionInfo.isLate && <LateNote>{i18next.t("The deadline has passed, but casting hasn't closed this one yet.")}</LateNote>}
          {/* Held back until the country resolves rather than shown with a
              default and swapped afterwards — that reads as a bait-and-switch
              on the one line about money. The row keeps its height while it
              waits, so the roles below do not jump. A free casting call needs
              no country: it is free wherever they are. */}
          <Reassurance>
            {geoResolved || submissionInfo.allowFreeSubmissions
              ? trialReassurance(
                  (geoGuess && geoGuess.countryCode) || "",
                  submissionInfo.trialDays,
                  !!submissionInfo.allowFreeSubmissions
                )
              : ""}
          </Reassurance>
        </Hero>

        <SectionTitle>{i18next.t("Open roles")}</SectionTitle>
        {linkRoleFilled && openRoles.length > 0 && (
          <FilledNotice role="status">
            {i18next.t("{{role}}, the role in your link, has been cast. These roles are still open.", {
              role: linkRole!.roleName,
            })}
          </FilledNotice>
        )}
        {linkRoleUnknown && openRoles.length > 0 && (
          <FilledNotice role="status">
            {i18next.t("The role in your link isn't part of this casting call. These roles are open.")}
          </FilledNotice>
        )}
        {openRoles.length === 0 && (
          <EmptyRoles>{i18next.t("No roles are open on this breakdown anymore.")}</EmptyRoles>
        )}
        {openRoles.map((role) => (
          <RoleCard
            key={role.roleId}
            selected={linkRoleOpen && role.roleId === linkRole!.roleId}
            aria-current={linkRoleOpen && role.roleId === linkRole!.roleId ? "true" : undefined}
          >
            <RoleName>{role.roleName}</RoleName>
            <SubmitButton
              disabled={loading}
              onClick={() => onSelectRole(role)}
              aria-label={`${i18next.t("Submit to")} ${role.roleName} — ${
                submissionInfo.breakdownName
              }`}
            >
              {i18next.t("Submit to this role")}
            </SubmitButton>
          </RoleCard>
        ))}
        {castRoles.map((role) => (
          <RoleCard key={role.roleId} dimmed>
            <RoleName>{role.roleName}</RoleName>
            <CastBadge>{i18next.t("Cast")}</CastBadge>
          </RoleCard>
        ))}
      </>
    );
  };

  return (
    <SubmissionLayout wide>
      {renderBody()}
      {isMockApi() && <ScenarioLauncher search={location.search} />}
    </SubmissionLayout>
  );
};

const Hero = styled.div`
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const ProjectType = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #53627c;
`;

const BreakdownName = styled.h1`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 22px;
  font-weight: 700;
  margin: 6px 0 10px;
  color: #122640;
`;

const TitleText = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

// Reads as the title, underlined and with an arrow so it reads as a link to
// another page, without turning the hero blue.
const OpensIcon = styled.span`
  font-size: 0.7em;
  margin-left: 6px;
  vertical-align: 0.15em;
`;

const TitleLink = styled.a`
  color: inherit;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 4px;

  /* The global stylesheet colours a:link / a:visited; keep the title's navy. */
  &:link,
  &:visited {
    color: #122640;
  }
  min-width: 0;
  overflow-wrap: anywhere;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #0584d7;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const FilledNotice = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: #53627c;
  background: #f5f7fa;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
`;

const ClosesPill: any = styled.span`
  /* Same blue as the list and the step header: white bold on #0f9bf0 was ~3:1. */
  background-color: ${(p: any) => (p.late ? "#ffe2b8" : p.isClosed ? "#8a94a6" : "#0584d7")};
  color: ${(p: any) => (p.late ? "#7a3f00" : "white")};
  font-size: 12px;
  font-weight: ${(p: any) => (p.late ? 700 : 600)};
  border-radius: 12px;
  padding: 3px 10px;
`;

// Expired but still open (isLate): why the roles below still take a submission.
const LateNote = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #7a3f00;
  margin-top: 8px;
`;

const CityLabel = styled.span`
  margin-left: 10px;
  font-size: 13px;
  color: #53627c;
`;

const Reassurance = styled.div`
  margin-top: 10px;
  font-size: 12px;
  line-height: 16px;
  /* Occupied even while the country is unknown, so the roles below do not jump
     when the lookup lands. Same rule as the blue header's own line. */
  min-height: 16px;
  color: #53627c;
`;

const SectionTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 10px;
`;

// The role the ad promoted wears the selected style — the same blue ring and
// tint a chosen option gets — so the visitor finds it at a glance. The ring is
// drawn with box-shadow so the card does not grow by a pixel.
const RoleCard: any = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${(p: any) => (p.selected ? "#0584d7" : "#e3e8ee")};
  box-shadow: ${(p: any) => (p.selected ? "0 0 0 1px #0584d7" : "none")};
  background-color: ${(p: any) => (p.selected ? "#eef7fd" : "transparent")};
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  opacity: ${(p: any) => (p.dimmed ? 0.55 : 1)};
`;

const RoleName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #122640;
`;

const SubmitButton = styled.button`
  ${membershipPrimary}
  padding: 10px 14px;
  min-height: 44px;
`;

const CastBadge = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #8a94a6;
`;

const EmptyRoles = styled.div`
  font-size: 13px;
  color: #53627c;
  margin-bottom: 10px;
`;

const ErrorBox = styled.div`
  border: 1px solid #f0c0c0;
  background: #fdf3f3;
  border-radius: 8px;
  padding: 16px;
  font-size: 14px;
  color: #7a2c2c;
`;

const RetryButton = styled.button`
  margin-top: 12px;
  background-color: #0f9bf0;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 16px;
  cursor: pointer;
`;

const SkeletonBlock: any = styled.div`
  height: ${(p: any) => p.height || 60}px;
  border-radius: 8px;
  background-color: #eef1f5;
  margin-bottom: 12px;
`;

interface StateProps {
  submissionInfo: iRootState["submissionFlowModel"]["submissionInfo"];
  serverOffsetMs: number;
  loading: boolean;
  error: any;
  geoGuess: iRootState["submissionFlowModel"]["geoGuess"];
  geoResolved: boolean;
}

interface DispatchProps {
  loadEntry: (payload: { breakdownId: string; roleId?: string }) => Promise<any>;
  loadGeoGuess: () => Promise<any>;
  startFlow: (payload: {
    breakdownId: string;
    roleId?: string;
    entrySource: string;
    history: any;
    search?: string;
  }) => Promise<any>;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  submissionInfo: state.submissionFlowModel.submissionInfo,
  serverOffsetMs: state.submissionFlowModel.serverOffsetMs,
  loading: state.submissionFlowModel.loading,
  error: state.submissionFlowModel.error,
  geoGuess: state.submissionFlowModel.geoGuess,
  geoResolved: state.submissionFlowModel.geoResolved,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  loadEntry: dispatch.submissionFlowModel.loadEntry,
  loadGeoGuess: dispatch.submissionFlowModel.loadGeoGuess,
  startFlow: dispatch.submissionFlowModel.startFlow,
});

export default connect(mapStateToProps, mapDispatchToProps)(BoostLandingPage);
